import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import { useBoolean } from 'react-use'

import { AIEmojisAPI } from '@makyoapp/makyo-sdk'
import { ColumnAdapter } from '@makyoapp/makyo-common'
import { Field, MaterialIconPicker, SelectDropdown, SimpleInput } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppOverlaysContext } from '@/context/AppOverlaysContext'
import { DEFAULT_COLUMN_TYPES } from '@/shared/builder'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { BuilderOverlay } from '@/shared/overlay'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'
import { RenderColumn } from '@/components/RenderColumn'

interface OverlaySettingDataProps {
  overlay: BuilderOverlay
  index: number
  search: string
}

const MAX_OVERLAY_TITLE_LENGTH = 20
const MIN_OVERLAY_TITLE_LENGTH = 2

export const OverlaySettingData = ({ overlay, index, search }: OverlaySettingDataProps) => {
  const { updateOverlay } = useAppOverlaysContext()
  const { tables } = useDbClientContext()

  // State lokal untuk mengelola data overlay yang sedang diedit
  const [localOverlay, setLocalOverlay] = useState<BuilderOverlay>(overlay)
  const [showIconPicker, setShowIconPicker] = useBoolean(false)
  const [tempTitle, setTempTitle] = useState(overlay.title)

  const [hasLocalChanges, setHasLocalChanges] = useState(false)

  // Update localOverlay ketika overlay prop berubah
  useEffect(() => {
    // Hanya update jika tidak ada perubahan lokal
    if (!hasLocalChanges) {
      setLocalOverlay(overlay)
    }
  }, [overlay, hasLocalChanges])

  const isTblPage = useMemo(() => {
    return Boolean(localOverlay.tableId)
  }, [localOverlay.tableId])

  useEffect(() => {
    setTempTitle(overlay.title)
  }, [overlay.title])

  const pageTable = useMemo(() => {
    return localOverlay.tableId ? tables.find((tb) => tb.id === localOverlay.tableId) : undefined
  }, [localOverlay.tableId, tables])

  const colOptions = useMemo(() => {
    return [
      { id: '', name: 'Select Column' } as ColumnAdapter,
      ...((pageTable &&
        pageTable.columns.filter((c) => {
          return DEFAULT_COLUMN_TYPES.includes(c.type)
        })) ||
        []),
    ]
  }, [pageTable])

  const updateLocalAndAPI = useCallback(
    (updater: (overlay: BuilderOverlay) => void) => {
      setLocalOverlay((prev) => {
        const updated = { ...prev }
        updater(updated)
        return updated
      })
      updateOverlay(index, updater)
      setHasLocalChanges(true)
    },
    [updateOverlay, index]
  )

  const localOverlayRef = useRef(localOverlay)
  useEffect(() => {
    localOverlayRef.current = localOverlay
  }, [localOverlay])

  const getFilteredAccordionItems = useCallback(() => {
    if (!search.trim()) {
      return {
        showPageSettings: true,
        showPageContent: !isTblPage,
        showTopBarSettings: true,
        showNavbarSettings: true,
        showSeoSettings: true,
        showBadgeSettings: !isTblPage,
      }
    }

    const searchLower = search.toLowerCase()

    return {
      showPageSettings: 'title'.includes(searchLower) || 'icon'.includes(searchLower),
    }
  }, [search, isTblPage])

  const filteredItems = getFilteredAccordionItems()

  return (
    <Accordion openMultiple={false} initialActiveItems={['page-settings']}>
      {filteredItems.showPageSettings && (
        <AccordionItem itemId='page-settings' title={<Trans>Title & Icon</Trans>}>
          <Field>
            <label className='w-[60px]'>
              <Trans>Title</Trans>
            </label>
            {isTblPage && (
              <SelectDropdown
                id='page-title-columns-source'
                withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
                onChange={async (col) => {
                  updateLocalAndAPI((prev) => {
                    prev.title = col.id
                  })
                }}
                value={colOptions.find((col) => localOverlay.title === col.id)}
                optionLabel={(col) => `${col?.id} ${col?.name}`}
                options={colOptions}
                renderOption={(opt) => {
                  return <RenderColumn withStyle={false} col={opt as unknown as ColumnAdapter} />
                }}
              />
            )}
            {!isTblPage && (
              <SimpleInput
                value={tempTitle}
                onChange={(e) => {
                  const value = e.currentTarget.value
                  if (value.trim().length > MAX_OVERLAY_TITLE_LENGTH) {
                    setTempTitle(value.slice(0, MAX_OVERLAY_TITLE_LENGTH))
                  } else {
                    setTempTitle(value)
                  }
                }}
                error={
                  tempTitle.trim().length < MIN_OVERLAY_TITLE_LENGTH ? (
                    <Trans>Required at least 2 characters.</Trans>
                  ) : tempTitle.trim().length > MAX_OVERLAY_TITLE_LENGTH ? (
                    <Trans>Max {MAX_OVERLAY_TITLE_LENGTH} characters.</Trans>
                  ) : undefined
                }
                onBlur={async () => {
                  if (tempTitle.trim() !== localOverlay.title.trim() && tempTitle.length >= MIN_OVERLAY_TITLE_LENGTH) {
                    setTempTitle(tempTitle.trim())
                    updateLocalAndAPI((prev) => {
                      prev.title = tempTitle.trim()
                    })
                  }
                }}
              />
            )}
          </Field>

          {/* //*Note: Overlay Slug is hidden for now */}
          {/* <Field>
          <label className='w-[60px]'>
            <Trans>Slug</Trans>
          </label>
          <div className='flex w-full pl-3'>
            <SimpleInput
              id='page-slug'
              className='rtl:[direction:ltr] w-full'
              inputClassName='w-full'
              value={slug}
              onChange={onInputChange}
              error={!validSlug ? <Trans>Invalid slug name</Trans> : slugError ? <Trans>{slugError}</Trans> : undefined}
            />
            <button onClick={handleUpdateSlug} disabled={slugLoading || !validSlug} className='mt-2 ml-2 h-fit'>
              {slugLoading ? (
                <span className='loading spin' />
              ) : (
                <CheckOrCross value={validSlug && slugError == undefined} />
              )}
            </button>
          </div>
        </Field> */}

          {!isTblPage && (
            <Field>
              <label className='w-full'>
                <Trans>Icon</Trans>
              </label>
              <MaterialIconPicker
                searchMs={300}
                searchFilter={async (keyword) => {
                  const aiSearch = await AIEmojisAPI.ReqFindEmoji(keyword)
                  return aiSearch
                }}
                onClose={() => setShowIconPicker(false)}
                show={showIconPicker}
                clickAsDone
                onToggle={() => setShowIconPicker(true)}
                onDone={async (icon) => {
                  updateLocalAndAPI((prev) => {
                    prev.icon = icon
                  })
                  setShowIconPicker(false)
                }}
                value={localOverlay.icon}
                className='h-fit rounded-md bg-zinc-200 dark:bg-slate-600'
                toggleClassName='p-1.5'
                portalMenu
                settings={{
                  fill: 0,
                  opsz: 24,
                  wght: 300,
                  grade: 0,
                }}
              />
            </Field>
          )}
        </AccordionItem>
      )}
    </Accordion>
  )
}
